import {
    accumulateMilestoneThresholds,
    buildCouponGroups,
    collapseToOptions,
    CouponGroup,
} from '~containers/road-to-rewards-configuration-page/components/MilestonesSection/coupon-groups';
import type {
    LoyaltyClubTierReward,
    RewardOptionCoupon,
} from '~stores/loyalty-club';
import type {
    CouponGroupMembersRow,
    PromotionActivityRow,
} from './use-campaign-promotion-activity';

/** Only the three fields this module actually reads off a tier: narrower
 * than the full `LoyaltyClubTierReward` the club response carries, so a test
 * fixture (and any future caller) need not fabricate an entire tier. */
export type TierRewardSkeleton = Pick<
    LoyaltyClubTierReward,
    'id' | 'position' | 'requiredPurchases' | 'rewardOptions'
>;

/** One coupon option's row in the rewards inventory table: the config
 * skeleton (name/image/stock) plus its joined activity, folded across
 * whichever regional promotion copies back this option AT THIS TIER. */
export interface RewardOptionRow {
    /** Unique across the whole table: a group key alone repeats across tiers
     * on purpose (the same coupon offered at two milestones), so the tier id
     * rides along to keep the two rows distinct. */
    key: string;
    name: string;
    image: string | null;
    /** `null` = unlimited (no configured cap, so no stock to sum). */
    quantity: number | null;
    /** `null` = unlimited, matching `quantity`. Never negative: the server
     * floors it at zero. */
    remaining: number | null;
    couponsIssued: number;
    couponsRedeemed: number;
    /** `null` on a zero denominator: an undefined rate, never a false 0%. */
    couponRedemptionRate: number | null;
    /** A distinct-member count, never a sum: a member holding the coupon in
     * two provinces is a row on each promotion, so summing double-counts
     * them. A one-copy row reads its own promotion row, already exact;
     * anything wider needs the served `couponGroup` row, and reads `null`
     * without one. */
    issuedMembers: number | null;
    redeemedMembers: number | null;
    memberRedemptionRate: number | null;
    expiredCoupons: number;
    /** Recomputed here, never served: API slice 05 left the per-coupon
     * expiration rate to the CMS rather than pin a metric nothing else reads.
     * Same zero-denominator guard as the two served rates. */
    expirationRate: number | null;
    /** Stock-out indicator: exactly a capped option at zero remaining. An
     * unlimited option (`remaining: null`) is never sold out. */
    isSoldOut: boolean;
}

export interface TierGroup {
    tierId: number;
    /** Cumulative visit threshold, not the per-tier delta. */
    visits: number;
    rows: RewardOptionRow[];
}

/**
 * A fold input's raw numerator for one rate, read straight off the served
 * cohort cell when the envelope carries it, falling back to reconstructing
 * it from `rate * denominator` for an envelope that predates that field.
 * A zero denominator reconstructs to `0` (nothing issued, nothing to poison
 * the fold with), never to `null`: only a genuinely unreadable rate on a
 * nonzero denominator is an unknown numerator that must propagate as `null`.
 */
const cohortFor = (
    cohort: number | undefined,
    rate: number | null,
    denominator: number
): number | null => {
    if (cohort != null) return cohort;
    if (denominator === 0) return 0;
    if (rate == null) return null;
    return Math.round(rate * denominator);
};

/** A rate from summed counts, `null` on a zero denominator or an unknown
 * numerator, rather than a divide-by-zero or a false 0%. */
const foldedRate = (
    numerator: number | null,
    denominator: number
): number | null =>
    numerator == null || denominator === 0 ? null : numerator / denominator;

/** A row no copy of which carries activity: no coupon was issued, so nobody
 * can hold one. The group axis serves no row for such a coupon, and this zero
 * is read off the coupon cells instead of waiting on one. */
const NO_MEMBERS = {
    issuedMembers: 0,
    redeemedMembers: 0,
    memberRedemptionRate: null,
} as const;

/** The `couponGroup` axis key, mirroring the API's own `couponGroupKey`: the
 * ladder placement, then the UPC the regional copies share or, for a coupon
 * that has none, the promotion that stands in for it. */
function couponGroupKeyOf(
    tierId: number,
    activationCode: string | null,
    promotionId: number
): string {
    return `${tierId}:${activationCode ?? `promotion-${promotionId}`}`;
}

function buildRow(
    tierId: number,
    group: CouponGroup,
    tierPromotionIds: ReadonlySet<number>,
    couponsById: ReadonlyMap<number, RewardOptionCoupon>,
    activity: ReadonlyMap<string, PromotionActivityRow>,
    groupMembers: ReadonlyMap<string, CouponGroupMembersRow>
): RewardOptionRow {
    // Only the promotions THIS tier actually offers. `group.promotionIds` can
    // span promotions from a DIFFERENT tier too: `buildCouponGroups` infers
    // its allocation sets from province overlap alone (`toAllocationSets` in
    // coupon-groups.ts), which correctly separates two tiers stocked with the
    // same UPC when their regional copies overlap on a province, but has
    // nothing to split on when the two tiers instead partition the country
    // (tier 1 = QC, tier 2 = the rest) — both tiers' promotions land in one
    // set there. Intersecting with this tier's own promotion ids makes "never
    // pool across tiers" hold structurally, not by borrowing that heuristic.
    const promotionIds = group.promotionIds.filter((id) =>
        tierPromotionIds.has(id)
    );

    const members = promotionIds
        .map((id) => couponsById.get(id))
        .filter((coupon): coupon is RewardOptionCoupon => coupon != null);

    const first = members[0];
    const name = first ? first.name ?? `Coupon ${first.id}` : group.label;
    const image = members.find((coupon) => coupon.image != null)?.image ?? null;

    // An orphan row's promotion is absent from the catalogue, so `members` is
    // empty and every stock figure would fold to 0, reading as sold out for a
    // coupon whose stock is merely unknown. Report it the way unlimited reads.
    const isUnlimited =
        members.length === 0 ||
        members.some((coupon) => coupon.quantity == null);
    const quantity = isUnlimited
        ? null
        : members.reduce((total, coupon) => total + (coupon.quantity ?? 0), 0);
    const remaining = isUnlimited
        ? null
        : members.reduce((total, coupon) => total + (coupon.remaining ?? 0), 0);

    let couponsIssued = 0;
    let couponsRedeemed = 0;
    let cohortRedeemed: number | null = 0;
    let expiredCoupons = 0;

    // Coupon cells only: a grant belongs to exactly one regional copy, so
    // summing them is exact. The member cells cannot be summed at all and
    // come off the served group row below.
    for (const promotionId of promotionIds) {
        const row = activity.get(String(promotionId));
        if (!row) continue; // No activity for this regional copy: contributes zeros.
        couponsIssued += row.couponsIssued;
        couponsRedeemed += row.couponsRedeemed;
        const redeemedCohort = cohortFor(
            row.couponRedemptionCohort,
            row.couponRedemptionRate,
            row.couponsIssued
        );
        cohortRedeemed =
            cohortRedeemed == null || redeemedCohort == null
                ? null
                : cohortRedeemed + redeemedCohort;
        expiredCoupons += row.expiredCoupons;
    }

    // A one-copy row has nothing to deduplicate, so its own promotion row is
    // already the exact member count. Preferred over the served group row,
    // not merely a fallback for it: the group spans every copy of the UPC in
    // the tier and carries no province, while this row may have been narrowed
    // to one region by the caller's own coupon filter.
    const soleActivity =
        promotionIds.length === 1
            ? activity.get(String(promotionIds[0]))
            : undefined;

    // Wider rows need the served group, which answers only when it was
    // counted over exactly this row's copies. Compared on the promotions that
    // HAVE activity, since a copy with none contributes no members and the
    // API's group never mentions it either.
    const activationCode = first?.activationCode ?? null;
    const served = groupMembers.get(
        couponGroupKeyOf(tierId, activationCode, promotionIds[0])
    );
    const withActivity = promotionIds.filter((promotionId) =>
        activity.has(String(promotionId))
    ).length;
    const servedMembers =
        withActivity === 0
            ? NO_MEMBERS
            : soleActivity ??
              (served?.promotionsCovered === withActivity ? served : undefined);

    return {
        key: `${tierId}-${group.key}`,
        name,
        image,
        quantity,
        remaining,
        couponsIssued,
        couponsRedeemed,
        couponRedemptionRate: foldedRate(cohortRedeemed, couponsIssued),
        issuedMembers: servedMembers?.issuedMembers ?? null,
        redeemedMembers: servedMembers?.redeemedMembers ?? null,
        memberRedemptionRate: servedMembers?.memberRedemptionRate ?? null,
        expiredCoupons,
        expirationRate: foldedRate(expiredCoupons, couponsIssued),
        isSoldOut: remaining === 0,
    };
}

/**
 * The rewards inventory table's full data, grouped by tier: the skeleton
 * comes from the ladder configuration (every tier, every option, with its
 * stock), and each option's activity is joined on by promotion id, folding
 * regional copies of one coupon into one row per tier by activation code
 * (never pooling stock or counts across tiers).
 *
 * Only tiers that actually offer coupon options (a non-empty
 * `rewardOptions`) become a group; every tier still contributes its own
 * delta to the running cumulative threshold, so a non-coupon tier between two
 * milestone tiers cannot throw off the ladder's numbering.
 */
export function buildRewardsInventory(
    tierRewards: TierRewardSkeleton[],
    coupons: RewardOptionCoupon[],
    activity: ReadonlyMap<string, PromotionActivityRow>,
    groupMembers: ReadonlyMap<string, CouponGroupMembersRow> = new Map(),
    // Every promotion id the catalogue has, in any region: lets a caller
    // that region-scopes `coupons` still tell "out of region" (no row, as
    // today) apart from "absent from the catalogue" (a row, see below).
    catalogPromotionIds: ReadonlySet<number> = new Set(
        coupons.map((coupon) => coupon.id)
    )
): TierGroup[] {
    const groups = buildCouponGroups(coupons);
    const groupsByKey = new Map(groups.map((group) => [group.key, group]));
    const couponsById = new Map(coupons.map((coupon) => [coupon.id, coupon]));

    const tiers: TierGroup[] = [];
    for (const { tier, visits } of accumulateMilestoneThresholds(tierRewards)) {
        const promotionIds = (tier.rewardOptions ?? []).map(
            (option) => option.promotionId
        );
        if (promotionIds.length === 0) continue;
        const tierPromotionIds = new Set(promotionIds);

        const options = collapseToOptions(promotionIds, groups);
        const coveredPromotionIds = new Set<number>();
        const rows = options
            .map((option) =>
                option.groupKey ? groupsByKey.get(option.groupKey) : undefined
            )
            .filter((group): group is CouponGroup => group != null)
            .map((group) => {
                for (const id of group.promotionIds) {
                    if (tierPromotionIds.has(id)) coveredPromotionIds.add(id);
                }
                return buildRow(
                    tier.id,
                    group,
                    tierPromotionIds,
                    couponsById,
                    activity,
                    groupMembers
                );
            });

        // A promotion the catalogue no longer returns at all (soft-deleted,
        // or its Road to Rewards flag cleared) has no CouponGroup:
        // collapseToOptions drops it silently. Give it its own row so its
        // activity is not lost from the table's totals. One still merely out
        // of the selected region is in `catalogPromotionIds` and correctly
        // gets no row, same as before.
        for (const promotionId of promotionIds) {
            if (coveredPromotionIds.has(promotionId)) continue;
            if (catalogPromotionIds.has(promotionId)) continue;
            const orphanGroup: CouponGroup = {
                key: `missing-${promotionId}`,
                label: `Coupon ${promotionId} (not in catalogue)`,
                promotionIds: [promotionId],
            };
            rows.push(
                buildRow(
                    tier.id,
                    orphanGroup,
                    tierPromotionIds,
                    couponsById,
                    activity,
                    groupMembers
                )
            );
        }

        rows.sort((a, b) => a.name.localeCompare(b.name));

        tiers.push({ tierId: tier.id, visits, rows });
    }

    return tiers;
}
